<template>
  <div>
    <div class="space-y-6">
      <!-- Top Row: Back Button and Admin Badge -->
      <div class="flex items-center justify-between">
        <button
          @click="$emit('back')"
          class="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span class="text-xl font-medium text-gray-700">Back</span>
        </button>

      </div>

    <!-- Attendees Card -->
    <div
      class="bg-white rounded-xl shadow-md p-6 flex flex-col"
      style="min-height: 600px"
    >
      <!-- Header -->
      <div
        class="border-b border-gray-200 pb-4 mb-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
      >
        <div>
          <h2 class="text-2xl font-semibold text-gray-900">
            {{ event.name }}
          </h2>
          <p class="text-base font-semibold text-gray-700 mt-1">
            {{ formatEventDateTime }}
          </p>
        </div>

        <!-- Summary Stats -->
        <div class="flex gap-4 text-sm font-medium">
          <!-- flex-wrap so the optional EXPECTED stat doesn't push the row off
               the edge of a phone -->
          <div class="flex flex-row flex-wrap md:flex-col gap-x-3 gap-y-1 md:gap-0 md:items-start">
            <p>
              <span class="text-gray-500">CHECKED-IN:</span>
              <span class="text-gray-700"> {{ checkedInCount }}</span>
            </p>
            <p>
              <span class="text-gray-500">TRIAL:</span>
              <span class="text-gray-700"> {{ trialCount }}</span>
            </p>
            <p v-if="pendingExpectedTrials.length > 0">
              <span class="text-yellow-600">EXPECTED:</span>
              <span class="text-gray-700"> {{ pendingExpectedTrials.length }}</span>
            </p>
            <p>
              <span class="text-gray-500">DROP-IN:</span>
              <span class="text-gray-700"> {{ dropInCount }}</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex-1 flex items-center justify-center">
        <svg
          class="animate-spin h-8 w-8 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <!-- Attendees Table -->
      <div v-else class="flex-1 space-y-4">
        <!-- Sort Row with Add Wrestler Button -->
        <div class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-500">Sort by:</span>
            <select
              v-model="sortBy"
              class="text-base border border-gray-300 rounded-lg pl-3 pr-8 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              <option
                v-for="option in sortOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <button
            @click="toggleAddWrestler"
            class="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition"
            :class="addingWrestler ? 'bg-gray-200 text-gray-700' : ''"
            :style="!addingWrestler ? 'background-color: var(--team-primary); color: var(--team-text);' : ''"
            @mouseenter="$event.currentTarget.style.opacity = '0.9'"
            @mouseleave="$event.currentTarget.style.opacity = '1'"
          >
            <svg
              v-if="!addingWrestler"
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            <svg
              v-else
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span>{{ addingWrestler ? 'Cancel' : 'Check In Wrestler' }}</span>
          </button>
        </div>

        <!-- Inline Wrestler Search -->
        <div v-if="addingWrestler" class="space-y-3">
          <div class="flex items-center gap-2 bg-gray-100 rounded-lg px-4 py-3">
            <svg
              class="w-5 h-5 text-gray-400 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
              />
            </svg>
            <input
              ref="addWrestlerInput"
              v-model="wrestlerSearchQuery"
              type="text"
              placeholder="Search wrestler name..."
              class="flex-1 bg-transparent text-base text-gray-700 placeholder-gray-400 outline-none border-none ring-0 focus:ring-0"
              @input="handleWrestlerSearch"
            />
            <svg
              v-if="wrestlerSearchLoading"
              class="animate-spin w-5 h-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p class="text-sm font-semibold text-gray-500">Admin mode: checking in a wrestler here skips all eligibility checks.</p>

          <!-- Search Results -->
          <div
            v-if="wrestlerSearchResults.length > 0"
            class="border border-gray-200 rounded-lg divide-y divide-gray-100"
          >
            <button
              v-for="wrestler in wrestlerSearchResults"
              :key="'search-' + wrestler.id"
              @click="selectWrestlerToAdd(wrestler)"
              :disabled="checkingInWrestler"
              class="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition text-left"
            >
              <img
                v-if="wrestler.avatar_thumbnail"
                :src="wrestler.avatar_thumbnail"
                class="w-10 h-10 rounded-full object-cover flex-shrink-0"
                alt=""
              />
              <span
                v-else
                class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0"
              >
                <svg class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                </svg>
              </span>
              <span class="flex-1 min-w-0">
                <span class="block text-sm font-medium text-gray-900 break-words">{{ wrestler.full_name }}</span>
                <span v-if="wrestler.age || wrestler.weight_class" class="block text-xs text-gray-500">
                  <template v-if="wrestler.weight_class">{{ wrestler.weight_class }} lbs</template>
                  <template v-if="wrestler.age && wrestler.weight_class"> · </template>
                  <template v-if="wrestler.age">{{ wrestler.age }} yo</template>
                </span>
              </span>
              <span
                class="px-3 py-1 rounded-md text-xs font-medium flex-shrink-0"
                style="background-color: var(--team-primary); color: var(--team-text);"
              >
                Check In
              </span>
            </button>
          </div>

          <!-- No Results -->
          <p
            v-else-if="wrestlerSearchQuery && !wrestlerSearchLoading && wrestlerSearchResults.length === 0"
            class="text-sm text-gray-500 text-center py-2"
          >
            No wrestlers found matching "{{ wrestlerSearchQuery }}"
          </p>
        </div>

        <!-- Expected Trials: booked this class through the family-facing picker
             but no check-in yet, so they never show up in the attendee list
             below until someone checks them in. -->
        <div
          v-if="pendingExpectedTrials.length > 0"
          class="border border-yellow-300 bg-yellow-50 rounded-lg p-4 space-y-3"
        >
          <div>
            <p class="text-base font-semibold text-yellow-900">
              Expected trials ({{ pendingExpectedTrials.length }})
            </p>
            <p class="text-sm text-yellow-800">
              Booked this class for a trial. Check them in when they arrive.
            </p>
          </div>
          <div
            v-for="expected in pendingExpectedTrials"
            :key="'expected-' + expected.prospect_id"
            class="flex items-center gap-3 bg-white border border-yellow-200 rounded-lg px-4 py-3"
          >
            <img
              v-if="expected.profile && expected.profile.avatar_thumbnail"
              :src="expected.profile.avatar_thumbnail"
              class="w-10 h-10 rounded-full object-cover flex-shrink-0"
              alt=""
            />
            <span
              v-else
              class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0"
            >
              <svg class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
              </svg>
            </span>
            <span class="flex-1 min-w-0">
              <span class="block text-sm font-medium text-gray-900 break-words">
                {{ expectedName(expected) }}
              </span>
              <span class="block text-xs text-gray-500">
                <template v-if="expected.session_name">{{ expected.session_name }}</template>
                <template v-else>Trial</template>
                <template v-if="formatWrestlerDetails(expected.profile) !== '-'">
                  · {{ formatWrestlerDetails(expected.profile) }}
                </template>
              </span>
            </span>
            <button
              @click="checkInExpectedTrial(expected)"
              :disabled="checkingInWrestler"
              class="px-3 py-1 rounded-md text-xs font-medium flex-shrink-0 disabled:opacity-50"
              style="background-color: var(--team-primary); color: var(--team-text);"
            >
              {{ checkingInExpectedId === expected.prospect_id ? 'Checking in…' : 'Check In' }}
            </button>
          </div>
        </div>

        <!-- No Attendees. Suppressed when trials are expected, so the screen
             doesn't say "no wrestlers" directly under a list of people we're
             waiting on. -->
        <div v-if="attendees.length === 0 && pendingExpectedTrials.length === 0" class="py-8 text-center">
          <p class="text-lg text-gray-500">No wrestlers for this class yet.</p>
        </div>

        <!-- Mobile Card Layout -->
        <div v-else class="lg:hidden space-y-3">
          <div
            v-for="attendee in sortedAttendees"
            :key="'mobile-' + attendee.id"
            class="border border-gray-200 rounded-lg p-4"
            :class="getMobileCardClasses(attendee)"
          >
            <!-- Top Row: Avatar and Name -->
            <div class="flex items-center gap-3">
              <!-- Avatar -->
              <div
                class="bg-gray-100 rounded-full p-1 flex-shrink-0"
                :class="{ 'cursor-pointer hover:ring-2 hover:ring-gray-300': attendee.profile && attendee.profile.avatar_thumbnail }"
                @click="openEnlargedPhoto(attendee)"
              >
                <img
                  v-if="attendee.profile && attendee.profile.avatar_thumbnail"
                  :src="attendee.profile.avatar_thumbnail"
                  :alt="attendee.profile.display_name"
                  class="w-10 h-10 rounded-full object-cover"
                />
                <svg
                  v-else
                  class="w-10 h-10 text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                  />
                </svg>
              </div>
              <!-- Name -->
              <span
                class="text-base font-semibold text-gray-900 cursor-pointer hover:underline"
                @click="openEnlargedPhoto(attendee)"
              >
                {{ getWrestlerName(attendee) }}
              </span>
            </div>

            <!-- Second Row: Status Dropdown -->
            <div class="mt-3 relative">
              <button
                @click="toggleDropdown(attendee.id)"
                class="inline-flex items-center justify-between gap-1 px-3 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap"
                :class="getStatusClasses(attendee)"
                :disabled="updatingStatus === attendee.id"
              >
                <span class="flex items-center gap-2">
                  <span
                    class="w-2 h-2 rounded-full"
                    :class="getStatusDotClass(attendee)"
                  ></span>
                  <span v-if="updatingStatus === attendee.id">...</span>
                  <span v-else>{{ getStatusText(attendee) }}</span>
                </span>
                <svg
                  class="w-4 h-4 transition-transform"
                  :class="{ 'rotate-180': openDropdown === attendee.id }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <!-- Dropdown Menu -->
              <div
                v-if="openDropdown === attendee.id"
                class="absolute left-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
              >
                <template v-for="(option, index) in statusOptions">
                  <div
                    v-if="option.isDestructive && index > 0"
                    :key="option.value + '-divider-mobile'"
                    class="border-t border-gray-200 my-1"
                  ></div>
                  <button
                    :key="option.value + '-mobile'"
                    @click="handleStatusAction(attendee, option.value)"
                    class="w-full px-3 py-2 text-left text-sm flex items-center gap-2"
                    :class="[
                      option.isDestructive
                        ? 'text-red-600 hover:bg-red-50'
                        : 'hover:bg-gray-100',
                      { 'bg-gray-50': !option.isDestructive && getStatusText(attendee) === option.label }
                    ]"
                  >
                    <span class="w-2 h-2 rounded-full" :class="option.dotClass"></span>
                    {{ option.label }}
                  </button>
                </template>
              </div>
            </div>

            <!-- Third Row: Details and Notes -->
            <div class="mt-3 flex items-center justify-between text-sm">
              <div class="flex items-center gap-3">
                <span class="text-gray-600">{{ formatWrestlerDetails(attendee.profile) }}</span>
                <span :class="getRegistrationColor(attendee)" class="font-medium">
                  {{ getRegistrationType(attendee) }}
                </span>
              </div>
              <!-- Notes Icon -->
              <button
                @click="startEditingNotes(attendee)"
                class="text-gray-400 hover:text-gray-600 transition"
                :class="{ 'text-blue-500': attendee.notes }"
                title="Add/Edit Notes"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </div>

            <!-- Notes Row -->
            <div v-if="attendee.notes || editingNotesId === attendee.id" class="mt-3">
              <div v-if="editingNotesId === attendee.id" class="flex items-center gap-2">
                <input
                  ref="notesInput"
                  v-model="draftNotes"
                  type="text"
                  class="flex-1 text-base border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="Add notes..."
                  @keyup.enter="saveNotes(attendee)"
                  @keyup.escape="cancelEditingNotes"
                />
                <button @click="saveNotes(attendee)" class="text-green-600 hover:text-green-700" :disabled="savingNotes">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button @click="cancelEditingNotes" class="text-gray-400 hover:text-gray-600">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p v-else class="text-sm text-gray-500 italic cursor-pointer hover:text-gray-700" @click="startEditingNotes(attendee)">
                {{ attendee.notes }}
              </p>
            </div>
          </div>
        </div>

        <!-- Desktop Table Layout -->
        <div v-if="attendees.length > 0" class="hidden lg:block">
          <!-- Table Header -->
          <div class="bg-gray-100 rounded-lg px-3 py-2 flex items-center">
            <span class="w-48 text-sm font-medium text-gray-500 flex-shrink-0">Wrestler</span>
            <span class="w-36 text-sm font-medium text-gray-500 flex-shrink-0">Details</span>
            <span class="w-28 text-sm font-medium text-gray-500 flex-shrink-0">Roster</span>
            <span class="flex-1 text-sm font-medium text-gray-500">Status</span>
          </div>

          <!-- Attendee Rows -->
          <div
            v-for="attendee in sortedAttendees"
            :key="'desktop-' + attendee.id"
            class="px-3 py-2 rounded-md transition"
            :class="getRowClasses(attendee)"
          >
            <!-- Main Row -->
            <div class="flex items-center">
              <!-- Wrestler Info -->
              <div class="w-48 flex items-center gap-2 flex-shrink-0">
                <!-- Avatar -->
                <div
                  class="bg-gray-100 rounded-full p-1 flex-shrink-0"
                  :class="{ 'cursor-pointer hover:ring-2 hover:ring-gray-300': attendee.profile && attendee.profile.avatar_thumbnail }"
                  @click="openEnlargedPhoto(attendee)"
                >
                  <img
                    v-if="attendee.profile && attendee.profile.avatar_thumbnail"
                    :src="attendee.profile.avatar_thumbnail"
                    :alt="attendee.profile.display_name"
                    class="w-6 h-6 rounded-full object-cover"
                  />
                  <svg
                    v-else
                    class="w-6 h-6 text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
                    />
                  </svg>
                </div>
                <!-- Name -->
                <span
                  class="text-sm font-medium text-gray-700 cursor-pointer hover:underline"
                  @click="openEnlargedPhoto(attendee)"
                >
                  {{ getWrestlerName(attendee) }}
                </span>
              </div>

              <!-- Details -->
              <span class="w-36 text-sm font-medium text-gray-700 flex-shrink-0">
                {{ formatWrestlerDetails(attendee.profile) }}
              </span>

              <!-- Registration Type -->
              <span
                class="w-28 text-sm font-medium flex-shrink-0"
                :class="getRegistrationColor(attendee)"
              >
                {{ getRegistrationType(attendee) }}
              </span>

              <!-- Status Dropdown -->
              <div class="flex-1 flex items-center gap-2">
                <div class="relative">
                  <button
                    @click="toggleDropdown(attendee.id)"
                    class="inline-flex items-center justify-between gap-1 px-3 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap"
                    :class="getStatusClasses(attendee)"
                    :disabled="updatingStatus === attendee.id"
                  >
                    <span class="flex items-center gap-2">
                      <span
                        class="w-2 h-2 rounded-full"
                        :class="getStatusDotClass(attendee)"
                      ></span>
                      <span v-if="updatingStatus === attendee.id">...</span>
                      <span v-else>{{ getStatusText(attendee) }}</span>
                    </span>
                    <svg
                      class="w-4 h-4 transition-transform"
                      :class="{ 'rotate-180': openDropdown === attendee.id }"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <!-- Dropdown Menu -->
                  <div
                    v-if="openDropdown === attendee.id"
                    class="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
                  >
                    <template v-for="(option, index) in statusOptions">
                      <div
                        v-if="option.isDestructive && index > 0"
                        :key="option.value + '-divider-desktop'"
                        class="border-t border-gray-200 my-1"
                      ></div>
                      <button
                        :key="option.value + '-desktop'"
                        @click="handleStatusAction(attendee, option.value)"
                        class="w-full px-3 py-2 text-left text-sm flex items-center gap-2"
                        :class="[
                          option.isDestructive
                            ? 'text-red-600 hover:bg-red-50'
                            : 'hover:bg-gray-100',
                          { 'bg-gray-50': !option.isDestructive && getStatusText(attendee) === option.label }
                        ]"
                      >
                        <span
                          class="w-2 h-2 rounded-full"
                          :class="option.dotClass"
                        ></span>
                        {{ option.label }}
                      </button>
                    </template>
                  </div>
                </div>

                <!-- Notes Icon -->
                <button
                  @click="startEditingNotes(attendee)"
                  class="text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                  :class="{ 'text-blue-500': attendee.notes }"
                  title="Add/Edit Notes"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Notes Row (shown if notes exist or editing) -->
            <div
              v-if="attendee.notes || editingNotesId === attendee.id"
              class="mt-2 pl-10"
            >
              <div v-if="editingNotesId === attendee.id" class="flex items-center gap-2">
                <input
                  ref="notesInput"
                  v-model="draftNotes"
                  type="text"
                  class="flex-1 text-base border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="Add notes..."
                  @keyup.enter="saveNotes(attendee)"
                  @keyup.escape="cancelEditingNotes"
                />
                <button
                  @click="saveNotes(attendee)"
                  class="text-green-600 hover:text-green-700"
                  :disabled="savingNotes"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  @click="cancelEditingNotes"
                  class="text-gray-400 hover:text-gray-600"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p
                v-else
                class="text-sm text-gray-500 italic cursor-pointer hover:text-gray-700"
                @click="startEditingNotes(attendee)"
              >
                {{ attendee.notes }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Enlarged Photo Modal -->
    <div
      v-if="enlargedPhotoAttendee"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeEnlargedPhoto"
    >
      <div class="bg-white rounded-xl p-6 md:p-8 max-w-sm md:max-w-lg w-full mx-4 relative">
        <!-- Close Button -->
        <button
          @click="closeEnlargedPhoto"
          class="absolute top-3 right-3 md:top-4 md:right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <svg class="w-6 h-6 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Photo and Name -->
        <div class="flex flex-col items-center">
          <img
            :src="getEnlargedPhotoUrl(enlargedPhotoAttendee)"
            :alt="getWrestlerName(enlargedPhotoAttendee)"
            class="w-64 h-64 md:w-96 md:h-96 rounded-full object-cover border-4 border-gray-200"
          />
          <h3 class="mt-4 text-xl font-semibold text-gray-900">
            {{ getWrestlerName(enlargedPhotoAttendee) }}
          </h3>
          <p class="text-gray-500 mt-1">
            {{ formatWrestlerDetails(enlargedPhotoAttendee.profile) }}
          </p>
          <p
            v-if="lastLoggedWeight(enlargedPhotoAttendee.profile)"
            class="text-gray-500 mt-1"
          >
            Last logged: {{ lastLoggedWeight(enlargedPhotoAttendee.profile) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KioskAttendeesView',
  props: {
    store: {
      type: Object,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    selectedDate: {
      type: Date,
      required: true,
    },
  },
  emits: ['back', 'add-wrestler-checkin'],
  data() {
    return {
      attendees: [],
      loading: true,
      openDropdown: null,
      updatingStatus: null,
      enlargedPhotoAttendee: null,
      statusOptions: [
        { value: 'present', label: 'On the Mat', dotClass: 'bg-green-500' },
        { value: 'late', label: 'Late', dotClass: 'bg-yellow-500' },
        { value: 'excused', label: 'Excused', dotClass: 'bg-gray-500' },
        { value: 'unexcused', label: 'Unexcused', dotClass: 'bg-red-500' },
        { value: 'injured', label: 'Injured', dotClass: 'bg-orange-500' },
        { value: 'other', label: 'Other', dotClass: 'bg-gray-400' },
        {
          value: 'remove', label: 'Remove', dotClass: 'bg-red-600', isDestructive: true,
        },
      ],
      sortBy: 'last_name',
      editingNotesId: null,
      draftNotes: '',
      savingNotes: false,
      sortOptions: [
        { value: 'last_name', label: 'Last Name' },
        { value: 'first_name', label: 'First Name' },
        { value: 'weight', label: 'Weight' },
        { value: 'age', label: 'Age' },
      ],
      // Inline add wrestler
      addingWrestler: false,
      wrestlerSearchQuery: '',
      wrestlerSearchResults: [],
      wrestlerSearchLoading: false,
      wrestlerSearchTimeout: null,
      checkingInWrestler: false,
      // Trials booked into this class (prospects.trial_event_id). Loaded
      // separately from the check-ins because a booking has no check-in record
      // until they walk in.
      expectedTrials: [],
      checkingInExpectedId: null,
    };
  },
  computed: {
    sortedAttendees() {
      return [...this.attendees].sort((a, b) => {
        const profileA = a.profile || {};
        const profileB = b.profile || {};
        const nameA = profileA.display_name || '';
        const nameB = profileB.display_name || '';

        switch (this.sortBy) {
          case 'last_name':
            // Extract last name from display_name (second word)
            const lastNameA = nameA.split(' ')[1] || '';
            const lastNameB = nameB.split(' ')[1] || '';
            return lastNameA.localeCompare(lastNameB);
          case 'first_name':
            // Extract first name from display_name (first word)
            const firstNameA = nameA.split(' ')[0] || '';
            const firstNameB = nameB.split(' ')[0] || '';
            return firstNameA.localeCompare(firstNameB);
          case 'weight':
            return (profileA.weight_class || 0) - (profileB.weight_class || 0);
          case 'age':
            // Sort by date_of_birth (older first)
            const dobA = profileA.date_of_birth
              ? new Date(profileA.date_of_birth)
              : new Date(0);
            const dobB = profileB.date_of_birth
              ? new Date(profileB.date_of_birth)
              : new Date(0);
            return dobA - dobB;
          default:
            return 0;
        }
      });
    },
    formatEventDateTime() {
      const time = this.formatTime(this.event.start_at);
      const dateOptions = { weekday: 'short', month: 'short', day: 'numeric' };
      const dateStr = this.selectedDate
        .toLocaleDateString('en-US', dateOptions)
        .toUpperCase()
        .replace(',', '');
      return `${time}, ${dateStr}`;
    },
    checkedInCount() {
      // Present or late counts as checked-in
      return this.attendees.filter(
        (a) => a.status === 'present' || a.status === 'late',
      ).length;
    },
    dropInCount() {
      return this.attendees.filter(
        (a) => this.getSpecialType(a) === 'Drop-in',
      ).length;
    },
    trialCount() {
      return this.attendees.filter(
        (a) => this.getSpecialType(a) === 'Trial',
      ).length;
    },
    // Matched on wrestler_profile_id, not check-in id — any check-in for that
    // wrestler (however it was created) means they're accounted for and the
    // expected row drops off.
    pendingExpectedTrials() {
      const checkedIn = new Set(this.attendees.map((a) => a.wrestler_profile_id));
      return this.expectedTrials.filter((t) => !checkedIn.has(t.wrestler_profile_id));
    },
  },
  watch: {
    // The parent clears this when an admin check-in fails, so mirror it to
    // release the expected row's button instead of leaving it stuck spinning.
    checkingInWrestler(inFlight) {
      if (!inFlight) this.checkingInExpectedId = null;
    },
  },
  mounted() {
    this.loadAttendees();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.wrestlerSearchTimeout) {
      clearTimeout(this.wrestlerSearchTimeout);
      this.wrestlerSearchTimeout = null;
    }
  },
  methods: {
    async loadAttendees() {
      this.loading = true;
      // Expected trials are supplementary — a failure there shouldn't blank the
      // attendee list, so it resolves to an empty array rather than throwing.
      this.loadExpectedTrials();
      try {
        const url = this.$apiService.eventCheckInsUrl(this.event.id, 'all');
        this.attendees = await this.$apiService.loadAllPages(url, 'check_ins');
      } catch (error) {
        console.error('Failed to load attendees:', error);
        this.$notificationManager.emit(
          'show-toast',
          'Failed to load attendees',
          false,
        );
      } finally {
        this.loading = false;
      }
    },
    async loadExpectedTrials() {
      try {
        const url = this.$apiService.eventExpectedTrialsUrl(this.event.id);
        const response = await axios.get(url);
        this.expectedTrials = (response.data && response.data.expected_trials) || [];
      } catch (error) {
        console.error('Failed to load expected trials:', error);
        this.expectedTrials = [];
      }
    },
    expectedName(expected) {
      return expected.profile?.display_name || 'Unknown';
    },
    // Reuses the admin add-wrestler path: same event, same skip-eligibility
    // check-in the front desk would do by searching for them by hand. The
    // parent bumps attendeesRefreshKey on success, which remounts this view and
    // reloads both lists, so the expected row drops off on its own.
    checkInExpectedTrial(expected) {
      this.checkingInExpectedId = expected.prospect_id;
      this.checkingInWrestler = true;
      this.$emit('add-wrestler-checkin', {
        id: expected.wrestler_profile_id,
        full_name: this.expectedName(expected),
      });
    },
    formatTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },
    getWrestlerName(attendee) {
      if (attendee && attendee.profile && attendee.profile.display_name) {
        return attendee.profile.display_name;
      }
      return 'Unknown';
    },
    // "142.6 lbs · Oct 1" from the profile's latest_weight* columns (see
    // WrestlerProfile#sync_latest_weight!), only when the team tracks weight
    lastLoggedWeight(profile) {
      if (!this.store.state.teamInfo?.weight_tracking_enabled) return null;
      if (!profile || !profile.latest_weight) return null;

      const weighedAt = profile.latest_weighed_at ? ` · ${moment(profile.latest_weighed_at).format('MMM D')}` : '';
      return `${profile.latest_weight} ${profile.latest_weight_units}${weighedAt}`;
    },
    formatWrestlerDetails(profile) {
      if (!profile) return '-';
      const parts = [];
      if (profile.weight_class) parts.push(`${profile.weight_class} lbs`);
      if (profile.date_of_birth) {
        const age = this.calculateAge(profile.date_of_birth);
        if (age) parts.push(`${age} yo`);
      }
      return parts.length > 0 ? parts.join(' / ') : '-';
    },
    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return null;
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0
        || (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age;
    },
    classPassSessionType(attendee) {
      return attendee.class_pass?.paid_session?.session_type || null;
    },
    getRegistrationType(attendee) {
      // Check for trial or drop-in based on class_pass (these are special)
      const sessionType = this.classPassSessionType(attendee);
      if (sessionType === 'trial') return 'Trial';
      if (sessionType === 'dropin') return 'Drop-in';

      // Find matching rosters between event and attendee
      // Use check-in snapshotted rosters (from metadata) rather than live profile rosters
      const eventRosterIds = this.event.roster_events?.map((re) => re.roster?.id) || [];
      const attendeeRosters = attendee.rosters || [];

      if (eventRosterIds.length > 0 && attendeeRosters.length > 0) {
        const matchingRosters = attendeeRosters.filter((r) => eventRosterIds.includes(r.id));
        if (matchingRosters.length > 0) {
          return matchingRosters.map((r) => r.name).join(', ');
        }
      }

      return 'Unknown';
    },
    isTrialOrDropIn(attendee) {
      // Check directly for class_pass since getRegistrationType now returns roster names
      const sessionType = this.classPassSessionType(attendee);
      return sessionType === 'trial' || sessionType === 'dropin';
    },
    getSpecialType(attendee) {
      // Returns "Trial", "Drop-in", or null
      const sessionType = this.classPassSessionType(attendee);
      if (sessionType === 'trial') return 'Trial';
      if (sessionType === 'dropin') return 'Drop-in';
      return null;
    },
    getRowClasses(attendee) {
      const specialType = this.getSpecialType(attendee);
      if (specialType === 'Trial') {
        return 'bg-green-50 hover:bg-green-100 border-l-4 border-green-500';
      }
      if (specialType === 'Drop-in') {
        return 'bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-500';
      }
      return 'hover:bg-gray-50';
    },
    getMobileCardClasses(attendee) {
      const specialType = this.getSpecialType(attendee);
      if (specialType === 'Trial') {
        return 'bg-green-50 border-green-500';
      }
      if (specialType === 'Drop-in') {
        return 'bg-blue-50 border-blue-500';
      }
      return '';
    },
    getRegistrationColor(attendee) {
      const specialType = this.getSpecialType(attendee);
      if (specialType === 'Trial') {
        return 'text-green-600';
      }
      if (specialType === 'Drop-in') {
        return 'text-blue-600';
      }
      return 'text-gray-700';
    },
    toggleDropdown(attendeeId) {
      if (this.openDropdown === attendeeId) {
        this.openDropdown = null;
      } else {
        this.openDropdown = attendeeId;
      }
    },
    handleClickOutside(event) {
      // Close dropdown if clicking outside of it
      if (this.openDropdown && !event.target.closest('.relative')) {
        this.openDropdown = null;
      }
    },
    handleStatusAction(attendee, actionValue) {
      if (actionValue === 'remove') {
        this.removeCheckIn(attendee);
      } else {
        this.updateStatus(attendee, actionValue);
      }
    },
    async removeCheckIn(attendee) {
      this.openDropdown = null;
      this.updatingStatus = attendee.id;

      try {
        const url = this.$apiService.updateCheckInUrl(attendee.id);
        await axios.delete(url);

        // Remove the attendee from the local list
        const index = this.attendees.findIndex((a) => a.id === attendee.id);
        if (index !== -1) {
          this.attendees.splice(index, 1);
        }

        this.$notificationManager.emit(
          'show-toast',
          'Check-in removed',
          true,
        );
      } catch (error) {
        console.error('Failed to remove check-in:', error);
        const serverMessage = error?.response?.data?.errors?.[0];
        this.$notificationManager.emit(
          'show-toast',
          serverMessage || 'Failed to remove check-in',
          false,
        );
      } finally {
        this.updatingStatus = null;
      }
    },
    async updateStatus(attendee, statusValue) {
      this.openDropdown = null;
      this.updatingStatus = attendee.id;

      try {
        const url = this.$apiService.updateCheckInUrl(attendee.id);
        const params = { check_in: { status: statusValue } };

        const response = await axios.put(url, params);

        // Update the local attendee data
        const index = this.attendees.findIndex((a) => a.id === attendee.id);
        if (index !== -1 && response.data && response.data.check_in) {
          this.$set(this.attendees, index, response.data.check_in);
        }
      } catch (error) {
        console.error('Failed to update status:', error);
        this.$notificationManager.emit(
          'show-toast',
          'Failed to update status',
          false,
        );
      } finally {
        this.updatingStatus = null;
      }
    },
    startEditingNotes(attendee) {
      this.editingNotesId = attendee.id;
      this.draftNotes = attendee.notes || '';
      this.$nextTick(() => {
        if (this.$refs.notesInput) {
          // Handle array of refs from v-for
          const input = Array.isArray(this.$refs.notesInput)
            ? this.$refs.notesInput[0]
            : this.$refs.notesInput;
          if (input) input.focus();
        }
      });
    },
    cancelEditingNotes() {
      this.editingNotesId = null;
      this.draftNotes = '';
    },
    async saveNotes(attendee) {
      if (this.savingNotes) return;
      this.savingNotes = true;

      try {
        const url = this.$apiService.updateCheckInUrl(attendee.id);
        const params = { check_in: { notes: this.draftNotes } };

        const response = await axios.put(url, params);

        // Update the local attendee data
        const index = this.attendees.findIndex((a) => a.id === attendee.id);
        if (index !== -1 && response.data && response.data.check_in) {
          this.$set(this.attendees, index, response.data.check_in);
        }

        this.editingNotesId = null;
        this.draftNotes = '';
      } catch (error) {
        console.error('Failed to save notes:', error);
        this.$notificationManager.emit(
          'show-toast',
          'Failed to save notes',
          false,
        );
      } finally {
        this.savingNotes = false;
      }
    },
    getStatusText(attendee) {
      // Map API status to display text
      switch (attendee.status) {
        case 'present':
          return 'On the Mat';
        case 'late':
          return 'Late';
        case 'excused':
          return 'Excused';
        case 'unexcused':
          return 'Unexcused';
        case 'absent':
          return 'Absent';
        case 'injured':
          return 'Injured';
        case 'other':
          return 'Other';
        default:
          return 'On the Mat';
      }
    },
    getStatusClasses(attendee) {
      const { status } = attendee;
      switch (status) {
        case 'present':
          return 'bg-green-100 text-green-700';
        case 'late':
          return 'bg-yellow-100 text-yellow-700';
        case 'excused':
          return 'bg-gray-200 text-gray-600';
        case 'unexcused':
        case 'absent':
          return 'bg-red-100 text-red-700';
        case 'injured':
          return 'bg-orange-100 text-orange-700';
        case 'other':
          return 'bg-gray-200 text-gray-500';
        default:
          return 'bg-green-100 text-green-700';
      }
    },
    getStatusDotClass(attendee) {
      const { status } = attendee;
      switch (status) {
        case 'present':
          return 'bg-green-500';
        case 'late':
          return 'bg-yellow-500';
        case 'excused':
          return 'bg-gray-500';
        case 'unexcused':
        case 'absent':
          return 'bg-red-500';
        case 'injured':
          return 'bg-orange-500';
        case 'other':
          return 'bg-gray-400';
        default:
          return 'bg-green-500';
      }
    },
    toggleAddWrestler() {
      this.addingWrestler = !this.addingWrestler;
      if (this.addingWrestler) {
        this.wrestlerSearchQuery = '';
        this.wrestlerSearchResults = [];
        this.$nextTick(() => {
          if (this.$refs.addWrestlerInput) {
            this.$refs.addWrestlerInput.focus();
          }
        });
      } else {
        this.resetAddWrestler();
      }
    },
    resetAddWrestler() {
      this.wrestlerSearchQuery = '';
      this.wrestlerSearchResults = [];
      this.wrestlerSearchLoading = false;
      if (this.wrestlerSearchTimeout) {
        clearTimeout(this.wrestlerSearchTimeout);
      }
    },
    handleWrestlerSearch() {
      if (this.wrestlerSearchTimeout) {
        clearTimeout(this.wrestlerSearchTimeout);
      }

      if (!this.wrestlerSearchQuery) {
        this.wrestlerSearchResults = [];
        this.wrestlerSearchLoading = false;
        return;
      }

      this.wrestlerSearchLoading = true;

      this.wrestlerSearchTimeout = setTimeout(() => {
        const queryAtRequest = this.wrestlerSearchQuery;
        const url = this.$apiService.checkInWrestlerSearchUrl(this.wrestlerSearchQuery);

        axios
          .get(url)
          .then((response) => {
            if (this.wrestlerSearchQuery === queryAtRequest) {
              this.wrestlerSearchResults = response.data.wrestlers || [];
              this.wrestlerSearchLoading = false;
            }
          })
          .catch((error) => {
            console.error('Failed to search wrestlers:', error);
            if (this.wrestlerSearchQuery === queryAtRequest) {
              this.wrestlerSearchLoading = false;
            }
          });
      }, 200);
    },
    selectWrestlerToAdd(wrestler) {
      this.$emit('add-wrestler-checkin', wrestler);
      this.checkingInWrestler = true;
    },
    finishAddWrestler() {
      this.addingWrestler = false;
      this.checkingInWrestler = false;
      this.resetAddWrestler();
    },
    openEnlargedPhoto(attendee) {
      if (attendee.profile && attendee.profile.avatar_thumbnail) {
        this.enlargedPhotoAttendee = attendee;
      }
    },
    closeEnlargedPhoto() {
      this.enlargedPhotoAttendee = null;
    },
    getEnlargedPhotoUrl(attendee) {
      if (!attendee || !attendee.profile) return null;
      // Prefer full avatar if available, otherwise use thumbnail
      return attendee.profile.avatar || attendee.profile.avatar_thumbnail;
    },
  },
};
</script>
